import { useState } from "react";
import { signupApi } from "../api/signup";

interface OnboardingProps {
  status: string;
  email?: string;
  onComplete: () => void;
}

export default function Onboarding({ status, email, onComplete }: OnboardingProps) {
  const [step, setStep] = useState(() => {
    if (status === "pending_setup") return 2;
    return 1; // unregistered
  });

  const [companyName, setCompanyName] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSignup() {
    if (!companyName.trim() || !acceptedTerms) return;

    setSubmitting(true);
    setError(null);

    try {
      await signupApi.register({
        companyName: companyName.trim(),
        acceptedTerms: true,
      });
      // Move to pending_setup step
      setStep(2);
    } catch (err: any) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Step 1: Welcome + Registration ──────────────────────────

  if (step === 1) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          {/* Logo / header */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-lg font-bold text-white">
              S
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome to SIMSY
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Set up your account to manage your IoT connectivity
            </p>
          </div>

          {/* Signed-in info */}
          {email && (
            <div className="mb-6 rounded-lg bg-gray-50 p-3 text-center">
              <p className="text-xs text-gray-500">Signed in as</p>
              <p className="text-sm font-medium text-gray-900">{email}</p>
            </div>
          )}

          {/* Form */}
          <div className="space-y-4">
            <div>
              <label
                htmlFor="company-name"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Company Name
              </label>
              <input
                id="company-name"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Your company name"
                autoFocus
              />
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">
                I agree to the{" "}
                <a
                  href="https://s-imsy.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="https://s-imsy.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Privacy Policy
                </a>
              </span>
            </label>

            {error && (
              <div className="rounded-lg bg-red-50 p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <button
              onClick={handleSignup}
              disabled={!companyName.trim() || !acceptedTerms || submitting}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Creating Account..." : "Create Account"}
            </button>
          </div>

          {/* Footer */}
          <p className="mt-6 text-center text-xs text-gray-400">
            Powered by SIMSY IoT Platform
          </p>
        </div>
      </div>
    );
  }

  // ── Step 2: Pending Setup ───────────────────────────────────

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        {/* Animated clock icon */}
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
          <svg
            className="h-7 w-7 text-amber-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        <h1 className="mb-2 text-xl font-bold text-gray-900">
          Account Setup in Progress
        </h1>

        <p className="mb-2 text-sm text-gray-600">
          Your account has been created and is being configured by our team.
          This usually takes less than 24 hours.
        </p>

        {email && (
          <p className="mb-6 text-sm text-gray-500">
            We'll notify you at{" "}
            <span className="font-medium text-gray-700">{email}</span> when
            your account is ready.
          </p>
        )}

        {/* Status steps */}
        <div className="mb-6 rounded-lg bg-gray-50 p-4 text-left">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
                <svg className="h-3.5 w-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-sm text-gray-700">Account created</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100">
                <div className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
              </div>
              <span className="text-sm text-gray-700">
                Connectivity service configuration
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200">
                <div className="h-2 w-2 rounded-full bg-gray-400" />
              </div>
              <span className="text-sm text-gray-400">Top up & get started</span>
            </div>
          </div>
        </div>

        <button
          onClick={onComplete}
          className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
        >
          Check status again
        </button>
      </div>
    </div>
  );
}
